import React, { useState, useEffect, useCallback, useMemo } from 'react';
import timesheetScheduleService from '../../services/TimesheetScheduleService';
import { format, parseISO, isValid } from 'date-fns';
import { toast } from 'react-hot-toast';
import TimesheetScheduleTable from './TimesheetScheduleTable';
import ScheduleFormDialog from './ScheduleFormDialog';
import { Button } from '../ui/button';
import { Plus, Send } from 'lucide-react';
import Skeleton from './Skeleton';

const TimesheetSchedule = () => {
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);

  const [editingSchedule, setEditingSchedule] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  // eslint-disable-next-line no-undef
  const [selectedIds, setSelectedIds] = useState(new Set());

  // eslint-disable-next-line no-undef
  const userRole = localStorage.getItem('role');
  const isAdmin = userRole === 'Admin';
  // eslint-disable-next-line no-undef
  const userName = localStorage.getItem('name');

  const fetchSchedules = useCallback(
    async (isLoadMore = false) => {
      try {
        setLoading(true);

        const limit = isLoadMore ? 10 : 12;
        const currentOffset = isLoadMore ? offset : 0;

        const params = {
          limit,
          skip: currentOffset
        };

        if (!isAdmin) {
          params.status = 'Published';
        }

        const response = await timesheetScheduleService.getSchedules(params);

        if (response.data.status === 'Success') {
          const newSchedules = response.data.data;

          if (newSchedules.length < limit) {
            setHasMore(false);
          }

          if (isLoadMore) {
            setSchedules((prev) => [...prev, ...newSchedules]);
            setOffset((prev) => prev + newSchedules.length);
          } else {
            setSchedules(newSchedules);
            setOffset(newSchedules.length);
            if (newSchedules.length < limit) setHasMore(false);
            else setHasMore(true);
          }
        }
      } catch (error) {
        // eslint-disable-next-line no-console, no-undef
        console.error('Error fetching schedules:', error);
        toast.error('Failed to load schedules');
      } finally {
        setLoading(false);
      }
    },
    [isAdmin, offset]
  );

  useEffect(() => {
    fetchSchedules(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLoadMore = () => {
    fetchSchedules(true);
  };

  const handleSave = async (data) => {
    try {
      if (data.id) {
        await timesheetScheduleService.updateSchedule(data.id, data);
        toast.success('Schedule updated successfully');
      } else {
        await timesheetScheduleService.createSchedule(data);
        toast.success('Schedule created successfully');
      }
      fetchSchedules(false);
    } catch (error) {
      // eslint-disable-next-line no-console, no-undef
      console.error('Error saving schedule:', error);
      toast.error('Failed to save schedule');
    }
  };

  const handleBulkPublish = async () => {
    try {
      const scheduleIds = Array.from(selectedIds);

      await timesheetScheduleService.bulkPublish(scheduleIds);
      toast.success('Schedules published successfully');

      // Clear selection and refresh
      // eslint-disable-next-line no-undef
      setSelectedIds(new Set());
      fetchSchedules(false);
    } catch (error) {
      // eslint-disable-next-line no-console, no-undef
      console.error('Error publishing schedules:', error);
      toast.error('Failed to publish schedules');
    }
  };

  const handleAdd = () => {
    setEditingSchedule(null);
    setIsDialogOpen(true);
  };

  const handleDelete = async (scheduleId) => {
    // eslint-disable-next-line no-undef
    if (window.confirm('Are you sure you want to delete this schedule?')) {
      try {
        await timesheetScheduleService.deleteSchedule(scheduleId);
        toast.success('Schedule deleted successfully');
        fetchSchedules(false);
      } catch (error) {
        // eslint-disable-next-line no-console, no-undef
        console.error('Error deleting schedule:', error);
        toast.error('Failed to delete schedule');
      }
    }
  };

  // Group schedules by date
  const groupedSchedules = useMemo(() => {
    const groups = {};
    schedules.forEach((schedule) => {
      // Robust date parsing
      let dateObj;
      try {
        dateObj = parseISO(schedule.date);
        if (!isValid(dateObj)) throw new Error('Invalid date');
      } catch (e) {
        // Fallback or skip if date is bad
        return;
      }

      const dateKey = format(dateObj, 'yyyy-MM-dd');

      if (!groups[dateKey]) {
        groups[dateKey] = {
          date: schedule.date,
          schedules: [],
          publishedDate: schedule.publishedDate || null,
          scheduledDate: schedule.scheduledDate || null,
          status: schedule.status
        };
      }
      groups[dateKey].schedules.push(schedule);

      // If we find a published record in this group, update group metadata to reflect published state
      if (schedule.status === 'Published') {
        groups[dateKey].status = 'Published';
        if (schedule.publishedDate) {
          groups[dateKey].publishedDate = schedule.publishedDate;
        }
      }
    });

    // Convert to array and sort by date descending
    return Object.values(groups).sort((a, b) => new Date(b.date) - new Date(a.date));
  }, [schedules]);

  return (
    <div className="tw-space-y-6">
      {/* Page Title (Optional as per design preference, keeping it for context) */}
      <div className="tw-flex tw-flex-row tw-items-center tw-mb-4">
        <h1 className="tw-text-2xl tw-font-bold tw-mr-2">Roster</h1>
      </div>

      {isAdmin && (
        <div className="tw-flex tw-justify-end tw-gap-2">
          {selectedIds.size > 0 && (
            <Button
              onClick={handleBulkPublish}
              disabled={selectedIds.size === 0}
              variant="outline"
              className="tw-flex tw-items-center tw-gap-2 tw-text-blue-600 tw-border-blue-200 hover:tw-bg-blue-50"
            >
              <Send className="tw-h-4 tw-w-4" />
              Publish Selected ({selectedIds.size})
            </Button>
          )}
          <Button onClick={handleAdd} className="tw-flex tw-items-center tw-gap-2 tw-bg-blue-700 tw-text-white">
            <Plus className="tw-h-4 tw-w-4" />
            Add Schedule
          </Button>
        </div>
      )}

      {loading && schedules.length === 0 && <Skeleton />}

      {!loading && schedules.length === 0 && (
        <div className="tw-text-center tw-py-12 tw-text-muted-foreground tw-border tw-border-dashed tw-rounded-lg tw-bg-muted/20">
          No schedules found.
        </div>
      )}

      {groupedSchedules.map((group) => {
        // Determine display date: Published Date if status is Published, otherwise Roster Date
        const isPublished = group.status === 'Published';
        const displayDate = group.date;
        const publishedDate = isPublished && group.publishedDate ? group.publishedDate : group.scheduledDate;

        return (
          <div key={group.date} className="tw-bg-card tw-border tw-border-border tw-rounded-lg tw-shadow-sm tw-mb-4">
            <div className="tw-p-4 tw-border-b tw-flex tw-flex-col">
              <div className="tw-text-lg tw-font-semibold">
                Roster for {format(new Date(displayDate), 'd MMM yyyy')}
              </div>

              <div className="tw-text-sm tw-text-gray-500">
                {isPublished ? 'Published by' : 'Scheduled by'}: {userName || 'Admin'} on{' '}
                {isValid(new Date(publishedDate)) ? format(new Date(publishedDate), 'dd/MM/yyyy') : 'Invalid Date'}
              </div>
            </div>

            <div className="tw-p-4">
              <TimesheetScheduleTable
                schedules={group.schedules}
                isAdmin={isAdmin}
                onSave={handleSave}
                onDelete={handleDelete}
                onBulkPublish={handleBulkPublish} // Pass logical handler, though table calls onBulkPublish prop on button click
                editingSchedule={editingSchedule}
                setEditingSchedule={setEditingSchedule}
                isDialogOpen={isDialogOpen}
                setIsDialogOpen={setIsDialogOpen}
                selectedIds={selectedIds}
                setSelectedIds={setSelectedIds}
              />
            </div>
          </div>
        );
      })}

      {hasMore && (
        <div className="tw-p-4 tw-border-t tw-flex tw-justify-center">
          <button
            onClick={handleLoadMore}
            disabled={loading}
            className="tw-px-4 tw-py-2 tw-bg-blue-500 tw-text-white tw-rounded hover:tw-bg-blue-600 disabled:tw-opacity-50"
          >
            {loading ? 'Loading...' : 'Load More'}
          </button>
        </div>
      )}

      <ScheduleFormDialog
        open={isDialogOpen}
        onOpenChange={setIsDialogOpen}
        schedule={editingSchedule}
        onSave={handleSave}
      />
    </div>
  );
};

export default TimesheetSchedule;
